/* eslint-disable no-underscore-dangle */
/**
 * WHERE AN APPROVED RECEIPT'S COST GOES — the three outcomes, as the approve
 * screen has to offer them.
 *
 *   1. A SEPARATE COST OPERATOR. For our own drivers, a parking cost settles
 *      against a different record from the one that ran the trip
 *      (`AU-AA-670 Parkolás`, not `AU-AA-670 - Tóth Imre`), because it is
 *      paid on a different basis. The API derives that bucket from the CAR on
 *      the receipt (`cost_operator`), through a real link and not by matching
 *      a name — see the API's `expenseCostOperator.helper.ts`.
 *   2. UNDER THE OPERATOR THAT DID THE TRIP — the receipt's own operator.
 *      Where every receipt on a vehicle with no bucket goes by default, which
 *      means every third-party operator's.
 *   3. ABSORBED — no line at all. Always offered, never pre-selected while
 *      there is somebody to file the cost under.
 *
 * THE DEFAULT FILES A LINE; ABSORBING HAS TO BE CHOSEN. Until 2026-11-02 a
 * vehicle with no bucket pre-selected Absorbed, on the rule that a
 * third-party operator's agreed price already covers such costs. That is no
 * longer the policy — Mark, 2026-11-02: "It should create a line item under
 * that same operator." The old default had been quietly keeping money from
 * operators. Of Golden Transfer's 38 approved receipts, every one Mark
 * approved himself was filed by hand to Golden Transfer, and the 16 approved
 * on the default were absorbed: money it was owed and never got (Borza
 * Sándor E.V.: 2 of 3 the same way). Mark backfilled those 18 the same
 * night. A pre-selection is what people confirm, so it has to be
 * the answer that pays whoever laid the money out. What this rules out:
 * bringing back "third party → absorbed" as a default, here or in the copy.
 *
 * The point of pre-selecting is that the person CONFIRMS rather than decides.
 * Every outcome stays changeable, and `SEARCH` opens every operator for the
 * case nothing auto-matches.
 *
 * The sentence under the picker is built from the SAME `describeConsequences`
 * the adjustments form uses, so "+800 Ft will be added to …" reads the same
 * wherever the money is about to move.
 */
import { describeConsequences } from './orderLineAdjustments';

/** Approve the receipt and add no line. */
export const ABSORBED = 'absorbed';

/** Open the every-operator search (a choice of screen, never a saved value). */
export const SEARCH = 'search';

export const ABSORBED_LABEL = 'Absorbed — no line on this booking';

/**
 * The bucket the API derived for this receipt's vehicle and cost type, or
 * null. Null means "no bucket", not "absorb": a third-party vehicle maps
 * nothing, and its cost then goes to the trip operator.
 *
 * The API also sends null when its car lookup FAILED (`costOperatorsFor` in
 * DriverExpenseService), and the two cases look the same from here. So on an
 * OWN vehicle a failed lookup pre-selects the driver's record rather than its
 * bucket. The consequence sentence names that record before Approve; telling
 * the two cases apart would need a field from the API.
 */
export const costBucketOf = (expense) => {
  const bucket = expense?.cost_operator;
  return bucket && bucket.id ? bucket : null;
};

/**
 * The operator on this trip: the receipt's own `operator_id`, which is the
 * portal login that filed it (`DriverExpense.operator_id`, taken from the
 * principal). That is the record the booking names, except when a company's
 * two records are linked (`absorbed_ids`) and the trip is held by the other
 * one. In that case the API treats it as an operator that is not on the
 * booking: it files the line under the off-booking role (`roleForExpenseLine`)
 * and adds that record to the booking (`ensureCostOperatorOnBooking`).
 */
export const tripOperatorOf = (expense) => (expense?.operator_id ? {
  id: String(expense.operator_id),
  name: expense.operator_name || `Operator ${String(expense.operator_id).slice(0, 6)}`,
} : null);

/**
 * What the picker offers, in the order the rule puts them: the vehicle's own
 * cost bucket first (when there is one), then the operator that did the trip,
 * then Absorbed, then the search. The FIRST of them is the pre-selection
 * (`preselectedDestination`), so this order is also the default rule.
 *
 * Never the unfiltered list of 130 operators — picking one of those by hand
 * is how money landed on the wrong record. The search is a deliberate extra
 * step for the case nothing matched.
 */
export function costDestinationOptions(expense) {
  const bucket = costBucketOf(expense);
  const tripOperator = tripOperatorOf(expense);
  const options = [];

  if (bucket) {
    options.push({
      value: bucket.id,
      label: `${bucket.name || bucket.id} · cost bucket for this vehicle`,
      group: 'bucket',
    });
  }

  if (tripOperator && tripOperator.id !== bucket?.id) {
    options.push({
      value: tripOperator.id,
      label: `${tripOperator.name} · the operator on this trip`,
      group: 'onBooking',
    });
  }

  options.push({ value: ABSORBED, label: ABSORBED_LABEL, group: 'absorbed' });
  options.push({ value: SEARCH, label: 'Search all operators…', group: 'search' });

  return options;
}

/**
 * THE ANSWER, PRE-SELECTED: the first thing the picker offers. That is the
 * vehicle's bucket for this kind of cost, else the operator on this trip,
 * else Absorbed. The order in `costDestinationOptions` IS the default rule,
 * so reordering it changes what Approve does by default. Absorbed comes
 * first only for a receipt that names no operator, which the API never
 * stores (`DriverExpense.operator_id` is required), so in practice approving
 * on the default always files a line (the 2026-11-02 rule, see the header).
 *
 * A RECEIPT WITH NO BOOKING NEVER GETS HERE, and the check is deliberately
 * not repeated here. `ExpenseOrderLineService.addLineForExpense` refuses a
 * line without a booking whoever it is for, bucket and trip operator alike.
 * So `ReceiptReviewRow` (`canFileLine`) shows no picker for such a receipt,
 * and its plain Approve sends no line. That gate is the one place the rule
 * lives. A new screen that renders `ReceiptCostDestination` must keep it, or
 * Approve on such a receipt is refused by the API: visibly, and with nothing
 * filed.
 */
export const preselectedDestination = (expense) => costDestinationOptions(expense)[0].value;

/** The chosen operator's name, for the consequence sentence. */
export function destinationName(expense, destination, searched = null) {
  if (!destination || destination === ABSORBED || destination === SEARCH) return null;
  if (searched && String(searched.id) === String(destination)) return searched.name;

  const match = costDestinationOptions(expense)
    .find((option) => String(option.value) === String(destination));
  if (match?.group === 'bucket') return costBucketOf(expense)?.name || destination;
  if (match?.group === 'onBooking') return tripOperatorOf(expense)?.name || destination;

  return null;
}

/**
 * Said plainly, because the sentence used to justify absorbing: "That is the
 * rule for a third-party operator, whose agreed price already covers it."
 * That was the rule until 2026-11-02. It also made 16 absorbed Golden
 * Transfer receipts read as correct when they were money the operator was
 * owed.
 */
export const ABSORBED_CONSEQUENCE = 'Nothing will be added to this booking — the cost is absorbed, '
  + 'so whoever paid it is not reimbursed.';

/**
 * WHAT THIS WILL ACTUALLY DO, in one sentence, before it is done:
 * "+800 Ft will be added to AU-AA-670 Parkolás on this booking."
 *
 * A mis-attribution is free to fix while it is still on screen and expensive
 * afterwards — once it is a line, correcting it is an edit if nothing has
 * paid it and a reversal if something has.
 */
export function describeExpenseConsequence(expense, destination, searched = null) {
  if (!destination || destination === SEARCH) return '';
  if (destination === ABSORBED) return ABSORBED_CONSEQUENCE;

  const name = destinationName(expense, destination, searched) || 'the selected operator';
  const [sentence] = describeConsequences(
    { operator_adjustment: Number(expense?.amount) || 0, currency: expense?.currency },
    { operatorName: name },
  );

  return sentence || '';
}

/**
 * The `order_line` the API takes, or undefined for Absorbed — which is
 * approve-with-no-line, and stays possible on purpose.
 *
 * Only the operator is ever sent. The description, the amount, the currency,
 * the role and the receipt link are derived server-side from the receipt
 * itself, so the screen cannot file a line that says something the receipt
 * does not — and cannot break the description the duplicate guard depends on.
 */
export function orderLineFor(destination) {
  if (!destination || destination === ABSORBED || destination === SEARCH) return undefined;
  return { operator_id: String(destination) };
}

/**
 * Why this destination cannot take an operator-side amount, or ''. The API
 * refuses a line against an operator with no `settlement_currency` — a line
 * stored in an unverified currency silently corrupts profit — and saying so
 * up front beats finding out after pressing Approve.
 *
 * THE BUCKET ONLY. The view sends `settlement_currency` for `cost_operator`
 * but not for the trip operator, which is now the default for every
 * third-party receipt. A trip operator with no settlement currency is
 * therefore caught by the API on Approve, not warned about here. The claim
 * stays pending and the reason is shown, because PortalExpensesController
 * writes the line BEFORE the decision. Covering the trip operator here
 * needs the API to send its currency too.
 */
export function settlementWarningFor(expense, destination) {
  const bucket = costBucketOf(expense);
  if (!bucket || String(destination) !== String(bucket.id)) return '';
  if (bucket.settlement_currency) return '';

  return `${bucket.name || 'This cost operator'} has no settlement currency set, so the ledger will `
    + 'refuse the line. Set one on the operator, or approve this as absorbed.';
}

export default {
  ABSORBED,
  SEARCH,
  costBucketOf,
  preselectedDestination,
  tripOperatorOf,
  costDestinationOptions,
  destinationName,
  describeExpenseConsequence,
  orderLineFor,
  settlementWarningFor,
};
